import React, { useState, useMemo, useEffect } from "react";
import { Col, Form, InputNumber, Row } from "antd";
import { EntityType, PriceAdjustType } from "@/utils/enum";
import MarginFeedbackAlert from "@/components/MarginFeedbackAlert";

const VehicleItem = ({
  vehicleData,
  priceType,
  dealerPriceAdjustmentType,
  transporterPriceAdjustmentType,
  dealerPriceAdjustmentDirection,
  transporterPriceAdjustmentDirection,
  dealerPriceAdjustmentAmount,
  transporterPriceAdjustmentAmount,
  calcNewPriceByPercent,
  calcNewPriceByDollars,
  calcNewPriceToDollarAmount,
  priceAdjustmentFormRef,
  priceResetFlag,
  setShowLowMarginReasonInput,
}) => {
  const { year, make, model, vin, quote, vehicleId } = useMemo(() => {
    return {
      year: vehicleData.year,
      make: vehicleData.make,
      model: vehicleData.model,
      vin: vehicleData.vin,
      quote: vehicleData.quote,
      vehicleId: vehicleData.id,
    };
  }, [vehicleData]);

  const { originalDealerPrice, originalTransporterPrice } = useMemo(() => {
    const { dealerPrice, transporterPrice } = quote;

    return {
      originalDealerPrice: dealerPrice ?? 0,
      originalTransporterPrice: transporterPrice ?? 0,
    };
  }, [quote]);

  const [dealerPrice, setDealerPrice] = useState(0);
  const [transporterPrice, setTransporterPrice] = useState(0);

  useEffect(() => {
    handleAdjustPrices(
      originalDealerPrice,
      dealerPriceAdjustmentType,
      dealerPriceAdjustmentDirection,
      dealerPriceAdjustmentAmount,
      setDealerPrice,
    );
  }, [dealerPriceAdjustmentType, dealerPriceAdjustmentDirection, dealerPriceAdjustmentAmount]);

  useEffect(() => {
    handleAdjustPrices(
      originalTransporterPrice,
      transporterPriceAdjustmentType,
      transporterPriceAdjustmentDirection,
      transporterPriceAdjustmentAmount,
      setTransporterPrice,
    );
  }, [transporterPriceAdjustmentType, transporterPriceAdjustmentDirection, transporterPriceAdjustmentAmount]);

  const handleAdjustPrices = (
    originalPrice,
    priceAdjustmentType,
    priceAdjustmentDirection,
    priceAdjustmentAmount,
    setPrice,
  ) => {
    if (priceAdjustmentType === PriceAdjustType.ToDollarAmmount) {
      // Set to dollar amount
      const newPrice = calcNewPriceToDollarAmount(priceAdjustmentAmount);
      setPrice(isNaN(Number(newPrice)) ? 0 : newPrice);
    } else if (priceAdjustmentType === PriceAdjustType.ByDollarAmmount) {
      // Adjust by dollar amount
      const newPrice = calcNewPriceByDollars(originalPrice, priceAdjustmentAmount, priceAdjustmentDirection);

      setPrice(isNaN(Number(newPrice)) ? 0 : newPrice);
    } else if (priceAdjustmentType === PriceAdjustType.ByPercentage) {
      // Adjust by percent amount
      const newPrice = calcNewPriceByPercent(originalPrice, priceAdjustmentAmount, priceAdjustmentDirection);

      setPrice(isNaN(Number(newPrice)) ? 0 : newPrice);
    }
  };

  useEffect(() => {
    priceAdjustmentFormRef?.current?.setFieldsValue({
      [`${vehicleId}#newDealerPrice`]: dealerPrice,
      [`${vehicleId}#newTransporterPrice`]: transporterPrice,
    });

    setShowLowMarginReasonInput(false);
  }, [dealerPrice, transporterPrice, dealerPriceAdjustmentAmount, transporterPriceAdjustmentAmount]);

  useEffect(() => {
    setDealerPrice(originalDealerPrice);
    setTransporterPrice(originalTransporterPrice);
  }, [priceResetFlag]);

  return (
    <Row gutter={16} align="middle">
      <Col span={6} style={{ alignSelf: "flex-start" }}>
        <div>{`${year} ${make} ${model}`}</div>
        <div style={{ fontWeight: "normal", color: "rgba(0, 0, 0, 0.45)" }}>{vin}</div>
      </Col>
      <Col span={5} style={{ justifyContent: "center" }}>
        <Form.Item
          name={`${vehicleId}#newDealerPrice`}
          label="Dealer"
          rules={[{ required: true, message: "Please enter a price" }]}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={dealerPrice}
            onChange={setDealerPrice}
            formatter={value => {
              const str = `$ ${originalDealerPrice} -> $ ${value}`;

              return str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={value => {
              const pattern = /(\$ [\d,]+(?:\.\d{0,2})?) -> \$ ([\d,]+(?:\.\d{0,2})?)/;

              const match = value.replaceAll(",", "").match(pattern);
              const parsedString = match ? match[2] : "";

              return parsedString;
            }}
            step={5}
            disabled={priceType === EntityType.Transporter}
          />
        </Form.Item>
      </Col>
      <Col span={5}>
        <Form.Item
          name={`${vehicleId}#newTransporterPrice`}
          label="Transporter"
          rules={[{ required: true, message: "Please enter a price" }]}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            value={transporterPrice}
            onChange={setTransporterPrice}
            formatter={value => {
              const str = `$ ${originalTransporterPrice} -> $ ${value}`;
              return str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={value => {
              const pattern = /(\$ [\d,]+(?:\.\d{0,2})?) -> \$ ([\d,]+(?:\.\d{0,2})?)/;

              const match = value.replaceAll(",", "").match(pattern);
              const parsedString = match ? match[2] : "";

              return parsedString;
            }}
            step={5}
            disabled={priceType === EntityType.Dealer}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <MarginFeedbackAlert dealerPrice={Number(dealerPrice)} transporterPrice={Number(transporterPrice)} />
      </Col>
    </Row>
  );
};

export default VehicleItem;
